import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface ImageManagerProps {
  images: string[];
  onChange: (images: string[]) => void;
}

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  stage: 'compressing' | 'uploading';
  preview: string;
  originalSize: number;
  compressedSize?: number;
  error?: string;
}

const MAX_DIMENSION = 1200;
const QUALITY = 0.82;
const MAX_RAW_SIZE = 10 * 1024 * 1024; // 10 MB

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIMENSION);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width / height) * MAX_DIMENSION);
          height = MAX_DIMENSION;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('canvas context unavailable')); return; }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('compression failed')); return; }
          resolve(blob);
        },
        'image/webp',
        QUALITY,
      );
    };

    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('load failed')); };
    img.src = objectUrl;
  });
}

async function uploadToStorage(blob: Blob): Promise<string | null> {
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.webp`;
  const path = `products/${fileName}`;

  const { error } = await supabase.storage
    .from('product-images')
    .upload(path, blob, { contentType: 'image/webp', cacheControl: '3600', upsert: false });

  if (error) {
    console.error('Upload error:', error.message);
    return null;
  }

  const { data } = supabase.storage.from('product-images').getPublicUrl(path);
  return data.publicUrl;
}

export default function ImageManager({ images, onChange }: ImageManagerProps) {
  const [newUrl, setNewUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Queue of successfully uploaded URLs to append — avoids stale closure
  const pendingUrls = useRef<string[]>([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const appendUrl = useCallback((url: string) => {
    // Always use the very latest images snapshot
    onChangeRef.current([...imagesRef.current, url]);
  }, []);

  const updateEntry = useCallback((id: string, patch: Partial<UploadingFile>) => {
    setUploading(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  }, []);

  const removeEntry = useCallback((id: string, preview: string) => {
    setUploading(prev => prev.filter(u => u.id !== id));
    URL.revokeObjectURL(preview);
  }, []);

  const processSingleFile = useCallback(async (file: File, entry: UploadingFile) => {
    // ── Stage 1: Compress ────────────────────────────────────────
    updateEntry(entry.id, { stage: 'compressing', progress: 10 });

    let blob: Blob;
    try {
      blob = await compressImage(file);
      updateEntry(entry.id, { progress: 40, compressedSize: blob.size });
    } catch (err) {
      console.error('Compress failed:', err);
      updateEntry(entry.id, { error: 'فشل الضغط', progress: 0 });
      setTimeout(() => removeEntry(entry.id, entry.preview), 2500);
      return;
    }

    // ── Stage 2: Upload ──────────────────────────────────────────
    updateEntry(entry.id, { stage: 'uploading', progress: 60 });

    const url = await uploadToStorage(blob);

    if (url) {
      updateEntry(entry.id, { progress: 100 });
      // Small delay so user sees 100% before it disappears
      setTimeout(() => {
        appendUrl(url);
        removeEntry(entry.id, entry.preview);
      }, 300);
    } else {
      updateEntry(entry.id, { error: 'فشل الرفع — تحقق من الاتصال', progress: 0 });
      setTimeout(() => removeEntry(entry.id, entry.preview), 3000);
    }
  }, [updateEntry, removeEntry, appendUrl]);

  const processFiles = useCallback((files: File[]) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    const toProcess = files.filter(f => {
      if (!validTypes.includes(f.type)) return false;
      if (f.size > MAX_RAW_SIZE) return false;
      return true;
    });

    if (!toProcess.length) return;

    const entries: UploadingFile[] = toProcess.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: f.name,
      progress: 0,
      stage: 'compressing' as const,
      preview: URL.createObjectURL(f),
      originalSize: f.size,
    }));

    setUploading(prev => [...prev, ...entries]);

    // Process all files concurrently — each manages its own state
    entries.forEach((entry, i) => processSingleFile(toProcess[i], entry));
  }, [processSingleFile]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    processFiles(files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const addUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    if (!url.startsWith('http')) { setUrlError('يجب أن يبدأ الرابط بـ http'); return; }
    if (images.includes(url)) { setUrlError('هذا الرابط موجود بالفعل'); return; }
    onChange([...images, url]);
    setNewUrl('');
    setUrlError('');
  };

  const removeImage = (index: number) => onChange(images.filter((_, i) => i !== index));

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= images.length) return;
    const updated = [...images];
    [updated[from], updated[to]] = [updated[to], updated[from]];
    onChange(updated);
  };

  const updateUrl = (index: number, val: string) => {
    const updated = [...images];
    updated[index] = val;
    onChange(updated);
  };

  const stageLabel = (u: UploadingFile) => {
    if (u.error) return u.error;
    if (u.stage === 'compressing') return 'جاري الضغط...';
    if (u.progress === 100) return 'تم بنجاح!';
    return 'جاري الرفع...';
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-[#1a1a1a] bg-gray-50 scale-[1.01]'
            : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
            <i className="ri-upload-cloud-2-line text-xl text-gray-400"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">اسحب وأفلت الصور هنا</p>
            <p className="text-xs text-gray-400 mt-0.5">
              أو <span className="text-[#1a1a1a] font-medium underline">انقر للاختيار</span> من جهازك
            </p>
          </div>
          <p className="text-xs text-gray-300">PNG · JPG · WEBP · GIF — حتى 10 ميغابايت</p>
          <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1 mt-1">
            <i className="ri-magic-line text-xs text-gray-500"></i>
            <span className="text-xs text-gray-500">ضغط وتحويل WebP تلقائي</span>
          </div>
        </div>
      </div>

      {/* Uploading progress */}
      {uploading.length > 0 && (
        <div className="space-y-2">
          {uploading.map(u => (
            <div
              key={u.id}
              className={`flex items-center gap-3 rounded-lg p-2.5 border transition-all ${
                u.error
                  ? 'border-red-100 bg-red-50'
                  : u.progress === 100
                  ? 'border-emerald-100 bg-emerald-50'
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-200 flex-shrink-0">
                <img src={u.preview} alt={u.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs text-gray-600 truncate">{u.name}</p>
                  {u.compressedSize && !u.error && (
                    <span className="text-xs text-green-600 font-medium flex-shrink-0 whitespace-nowrap">
                      {formatSize(u.originalSize)} → {formatSize(u.compressedSize)}
                    </span>
                  )}
                </div>
                {u.error ? (
                  <p className="text-xs text-red-500">{u.error}</p>
                ) : (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-500 ${
                          u.progress === 100
                            ? 'bg-emerald-500'
                            : u.stage === 'compressing'
                            ? 'bg-amber-400'
                            : 'bg-[#1a1a1a]'
                        }`}
                        style={{ width: `${u.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{stageLabel(u)}</p>
                  </>
                )}
              </div>
              {u.progress === 100 ? (
                <i className="ri-check-line text-emerald-500 flex-shrink-0 text-sm"></i>
              ) : u.error ? (
                <i className="ri-error-warning-line text-red-400 flex-shrink-0 text-sm"></i>
              ) : (
                <i className="ri-loader-4-line animate-spin text-gray-400 flex-shrink-0 text-sm"></i>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Existing images list */}
      {images.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">الصور المضافة ({images.length})</p>
          {images.map((url, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 border border-gray-100">
              <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-200 flex-shrink-0">
                <img
                  src={url}
                  alt={`صورة ${i + 1}`}
                  className="w-full h-full object-cover object-top"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              {i === 0 && (
                <span className="text-xs bg-[#1a1a1a] text-white px-1.5 py-0.5 rounded-md flex-shrink-0">
                  رئيسية
                </span>
              )}
              <input
                type="text"
                value={url}
                onChange={e => updateUrl(i, e.target.value)}
                className="flex-1 text-xs bg-transparent outline-none text-gray-500 min-w-0"
                dir="ltr"
              />
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => moveImage(i, i - 1)}
                  disabled={i === 0}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-20 cursor-pointer"
                >
                  <i className="ri-arrow-up-s-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => moveImage(i, i + 1)}
                  disabled={i === images.length - 1}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-20 cursor-pointer"
                >
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* URL input */}
      <div>
        <p className="text-xs text-gray-400 mb-2">أو أضف رابط صورة خارجية</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newUrl}
            onChange={e => { setNewUrl(e.target.value); setUrlError(''); }}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl())}
            placeholder="https://..."
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#1a1a1a] transition-colors"
            dir="ltr"
          />
          <button
            type="button"
            onClick={addUrl}
            className="bg-[#1a1a1a] text-white rounded-lg px-3 py-2 text-xs font-medium hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
          >
            إضافة رابط
          </button>
        </div>
        {urlError && <p className="text-red-500 text-xs mt-1">{urlError}</p>}
      </div>
    </div>
  );
}
